(function() {
  'use strict';
  
  // Cache for collections to avoid multiple API calls
  let collectionsCache = null;
  let collectionsFetching = false;
  
  // Function to safely get search query from multiple sources
  function getSearchQuery() {
    try {
      // Try URL parameters first
      const urlParams = new URLSearchParams(window.location.search);
      let query = urlParams.get('q') || urlParams.get('query') || urlParams.get('search');
      
      if (query && query.trim()) {
        return query.trim();
      }
      
      // Try Boost PFS internal state
      if (window.boostPFS && window.boostPFS.filter) {
        if (window.boostPFS.filter.settings && window.boostPFS.filter.settings.search) {
          query = window.boostPFS.filter.settings.search.query;
        }
        if (!query && window.boostPFS.filter.params) {
          query = window.boostPFS.filter.params.q || window.boostPFS.filter.params.query;
        }
      }
      
      return query ? query.trim() : '';
    } catch (error) {
      // console.log('Error getting search query:', error);
      return '';
    }
  }
  
  // Function to trigger GA4 event
  function triggerGA4Event(query) {
    try {
      if (typeof gtag === 'function') {
        gtag('event', 'search_zero_results', {
          search_term: query
        });
        // console.log('GA4 search_zero_results event triggered for:', query);
      } else if (typeof dataLayer !== 'undefined') {
        dataLayer.push({
          'event': 'search_zero_results',
          'search_term': query
        });
        // console.log('DataLayer search_zero_results event triggered for:', query);
      }
    } catch (error) {
      // console.log('Error triggering GA4 event:', error);
    }
  }
  
  // Function to fetch all collections with pagination
  async function fetchAllCollections() {
    if (collectionsCache) {
      return collectionsCache;
    }
    
    if (collectionsFetching) {
      // Wait for ongoing fetch
      return new Promise((resolve) => {
        const checkCache = () => {
          if (collectionsCache) {
            resolve(collectionsCache);
          } else {
            setTimeout(checkCache, 100);
          }
        };
        checkCache();
      });
    }
    
    collectionsFetching = true;
    // console.log('Fetching all collections...');
    
    try {
      let allCollections = [];
      let page = 1;
      let hasMore = true;
      const limit = 250; // Maximum allowed by Shopify
      
      while (hasMore && page <= 10) { // Safety limit of 10 pages
        const response = await fetch(`/collections.json?limit=${limit}&page=${page}`);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        const collections = data.collections || [];
        
        // console.log(`Page ${page}: Found ${collections.length} collections`);
        
        if (collections.length === 0) {
          hasMore = false;
        } else {
          allCollections = allCollections.concat(collections);
          hasMore = collections.length === limit;
          page++;
        }
      }
      
      // console.log(`Total collections fetched: ${allCollections.length}`);
      collectionsCache = allCollections;
      collectionsFetching = false;
      return allCollections;
      
    } catch (error) {
      // console.error('Error fetching collections:', error);
      collectionsFetching = false;
      // Return fallback collections
      return [
        { handle: 'all', title: 'All Products', url: '/collections/all' },
        { handle: 'sale', title: 'Sale Items', url: '/collections/sale' },
        { handle: 'new', title: 'New Arrivals', url: '/collections/new' }
      ];
    }
  }
  
  // Function to find matching collections based on search query
  async function findMatchingCollections(query) {
    if (!query) return [];
    
    const collections = await fetchAllCollections();
    const queryTerms = query.toLowerCase().split(/\s+/).filter(term => term.length > 2);
    const matchedCollections = [];
    
    // console.log('Search terms:', queryTerms);
    
    for (const collection of collections) {
      const collectionTitle = collection.title.toLowerCase();
      const collectionHandle = collection.handle.toLowerCase();
      
      // Check if any query term matches collection title or handle
      const hasMatch = queryTerms.some(term => 
        collectionTitle.includes(term) || 
        collectionHandle.includes(term) ||
        term.includes(collectionTitle) ||
        term.includes(collectionHandle)
      );
      
      if (hasMatch) {
        matchedCollections.push({
          title: collection.title,
          handle: collection.handle,
          url: `/collections/${collection.handle}`,
          score: 10 // High score for direct matches
        });
      }
    }
    
    // console.log('Direct matches:', matchedCollections);
    
    // Get all potential similar collections
    const allSimilarCollections = findSimilarCollections(collections, matchedCollections, queryTerms);
    const totalAvailable = matchedCollections.length + allSimilarCollections.length;
    
    // Determine how many to show: if 6+ available show 6, if less than 6 show 3
    const targetCount = totalAvailable >= 6 ? 6 : 3;
    
    // Add similar collections to reach target count
    if (matchedCollections.length < targetCount) {
      const similarCollections = allSimilarCollections.slice(0, targetCount - matchedCollections.length);
      matchedCollections.push(...similarCollections);
    }
    
    // Sort by score (descending) and return target count
    return matchedCollections
      .sort((a, b) => (b.score || 0) - (a.score || 0))
      .slice(0, targetCount)
      .map(col => ({
        title: col.title,
        handle: col.handle,
        url: col.url
      }));
  }
  
  // Function to find similar collections using semantic analysis
  function findSimilarCollections(allCollections, matchedCollections, queryTerms) {
    const matchedHandles = new Set(matchedCollections.map(m => m.handle));
    const availableCollections = allCollections.filter(col => !matchedHandles.has(col.handle));
    
    // Equipment category families for industrial/equipment stores
    const categoryFamilies = {
      'pumps': ['pump', 'pumping', 'flow', 'water', 'fluid', 'circulation', 'pressure'],
      'valves': ['valve', 'control', 'flow', 'shut', 'ball', 'gate', 'check', 'relief'],
      'motors': ['motor', 'drive', 'electric', 'gear', 'servo', 'stepper', 'ac', 'dc'],
      'filters': ['filter', 'filtration', 'screen', 'strainer', 'cartridge', 'media'],
      'bearings': ['bearing', 'ball', 'roller', 'thrust', 'pillow', 'block'],
      'seals': ['seal', 'gasket', 'o-ring', 'packing', 'mechanical'],
      'tools': ['tool', 'wrench', 'socket', 'drill', 'bit', 'cutting', 'measuring'],
      'electrical': ['electrical', 'wire', 'cable', 'connector', 'switch', 'panel', 'junction'],
      'hydraulic': ['hydraulic', 'cylinder', 'hose', 'fitting', 'pump', 'valve', 'fluid'],
      'pneumatic': ['pneumatic', 'air', 'cylinder', 'valve', 'compressor', 'fitting'],
      'fasteners': ['bolt', 'screw', 'nut', 'washer', 'fastener', 'hardware', 'anchor'],
      'safety': ['safety', 'protection', 'ppe', 'glove', 'goggle', 'helmet', 'vest']
    };
    
    const similarCollections = [];
    
    // Step 1: Find collections that share common root words with matched collections
    if (matchedCollections.length > 0) {
      // console.log('Finding collections with shared root words...');
      
      for (const matched of matchedCollections) {
        const matchedWords = extractRootWords(matched.title);
        
        for (const collection of availableCollections) {
          const collectionWords = extractRootWords(collection.title);
          const sharedWords = matchedWords.filter(word => collectionWords.includes(word));
          
          if (sharedWords.length > 0) {
            similarCollections.push({
              title: collection.title,
              handle: collection.handle,
              url: `/collections/${collection.handle}`,
              score: 8 + sharedWords.length,
              reason: `Shares words: ${sharedWords.join(', ')}`
            });
          }
        }
      }
    }
    
    // Step 2: Find collections in the same category family
    // console.log('Finding collections in same category families...');
    
    const searchContext = [...queryTerms, ...matchedCollections.map(m => m.title.toLowerCase())].join(' ');
    
    for (const [family, keywords] of Object.entries(categoryFamilies)) {
      const familyMatch = keywords.some(keyword => searchContext.includes(keyword));
      
      if (familyMatch) {
        // console.log(`Found family match: ${family}`);
        
        for (const collection of availableCollections) {
          const collectionText = `${collection.title} ${collection.handle}`.toLowerCase();
          const keywordMatches = keywords.filter(keyword => collectionText.includes(keyword));
          
          if (keywordMatches.length > 0) {
            // Avoid duplicates
            const exists = similarCollections.some(s => s.handle === collection.handle);
            if (!exists) {
              similarCollections.push({
                title: collection.title,
                handle: collection.handle,
                url: `/collections/${collection.handle}`,
                score: 6 + keywordMatches.length,
                reason: `In ${family} family`
              });
            }
          }
        }
      }
    }
    
    // Step 3: Find collections with partial term matches
    // console.log('Finding collections with partial matches...');
    
    for (const term of queryTerms) {
      if (term.length > 3) { // Only for longer terms
        for (const collection of availableCollections) {
          const collectionText = `${collection.title} ${collection.handle}`.toLowerCase();
          
          // Check for partial matches (term contains collection word or vice versa)
          const words = collectionText.split(/\s+/);
          const partialMatches = words.filter(word => 
            word.length > 3 && (term.includes(word) || word.includes(term))
          );
          
          if (partialMatches.length > 0) {
            const exists = similarCollections.some(s => s.handle === collection.handle);
            if (!exists) {
              similarCollections.push({
                title: collection.title,
                handle: collection.handle,
                url: `/collections/${collection.handle}`,
                score: 4 + partialMatches.length,
                reason: `Partial match: ${partialMatches.join(', ')}`
              });
            }
          }
        }
      }
    }
    
    // Step 4: If still not enough, add some popular/general collections
    if (similarCollections.length < 4) {
      // console.log('Adding popular fallback collections...');
      
      const popularKeywords = ['all', 'new', 'sale', 'popular', 'best', 'featured', 'shop'];
      const generalCollections = availableCollections
        .filter(col => {
          const title = col.title.toLowerCase();
          return popularKeywords.some(keyword => title.includes(keyword)) ||
                 ['all', 'shop', 'products', 'catalog'].includes(col.handle);
        })
        .slice(0, 4 - similarCollections.length)
        .map(col => ({
          title: col.title,
          handle: col.handle,
          url: `/collections/${col.handle}`,
          score: 2,
          reason: 'Popular collection'
        }));
      
      similarCollections.push(...generalCollections);
    }
    
    // console.log('Similar collections found:', similarCollections.map(s => `${s.title} (${s.reason})`));
    
    // Remove duplicates and sort by score
    const uniqueCollections = similarCollections.filter((collection, index, self) => 
      index === self.findIndex(c => c.handle === collection.handle)
    );
    
    return uniqueCollections.sort((a, b) => (b.score || 0) - (a.score || 0));
  }
  
  // Helper function to extract root words from collection titles
  function extractRootWords(title) {
    const commonWords = ['and', 'or', 'the', 'a', 'an', 'in', 'on', 'at', 'for', 'with', 'by'];
    
    return title.toLowerCase()
      .replace(/[^\w\s]/g, ' ') // Remove punctuation
      .split(/\s+/)
      .filter(word => word.length > 2 && !commonWords.includes(word))
      .map(word => {
        // Simple stemming - remove common suffixes
        if (word.endsWith('s') && word.length > 3) word = word.slice(0, -1);
        if (word.endsWith('ing') && word.length > 5) word = word.slice(0, -3);
        if (word.endsWith('ed') && word.length > 4) word = word.slice(0, -2);
        return word;
      });
  }
  
  // Function to auto-fill HubSpot form with search query using placeholder text
  function autoFillHubSpotForm(query) {
    try {
      if (!query) return;
      
      const maxAttempts = 40; // Try for up to 20 seconds (40 * 500ms)
      let attempts = 0;
      
      // console.log('Starting auto-fill attempts for query:', query);
      
              const attemptFill = () => {
          attempts++;
          // console.log(`Attempt ${attempts}: Looking for HubSpot textarea...`);
        
        // Function to search in a document (main or iframe)
        const searchInDocument = (doc) => {
          const selectors = [
            'textarea[placeholder*="Describe the equipment you need help finding"]',
            'textarea[placeholder*="equipment you need help finding"]',
            'textarea[placeholder*="help finding"]',
            'textarea.hs-input.hs-fieldtype-textarea',
            'textarea[id*="message"]',
            '#message-7e3e28b7-d9dc-4128-9c19-b254d974131b'
          ];
          
          for (const selector of selectors) {
            const textarea = doc.querySelector(selector);
            if (textarea) {
              // console.log(`Found textarea with selector: ${selector} in document`);
              return textarea;
            }
          }
          return null;
        };
        
        // Search in main document first
        let textarea = searchInDocument(document);
        
        // If not found, search in iframes
        if (!textarea) {
          const iframes = document.querySelectorAll('iframe');
          for (const iframe of iframes) {
            try {
              if (iframe.contentDocument) {
                textarea = searchInDocument(iframe.contentDocument);
                if (textarea) {
                  // console.log('Found textarea in iframe');
                  break;
                }
              }
            } catch (e) {
              // Cross-origin iframe, can't access
              // console.log('Cannot access iframe (cross-origin)');
            }
          }
        }
        
        if (textarea && (textarea.offsetParent !== null || textarea.style.display !== 'none')) {
          // console.log('Found visible textarea, starting typing animation...');
          
          const message = `I'm looking for ${query}`;
          let index = 0;
          
          // Clear existing content
          textarea.value = '';
          textarea.focus();
          
          // Typing animation
          const typeChar = () => {
            if (index < message.length) {
              textarea.value += message.charAt(index);
              // Trigger input event for form validation
              textarea.dispatchEvent(new Event('input', { bubbles: true }));
              index++;
              setTimeout(typeChar, 50); // 50ms delay between characters
            }
          };
          
          // Start typing after a short delay
          setTimeout(typeChar, 300);
          return;
        } else {
          // console.log(`No textarea found, trying again in 500ms...`);
        }
        
        if (attempts < maxAttempts) {
          setTimeout(attemptFill, 500);
        } else {
          // console.log('Could not find HubSpot textarea after maximum attempts');
        }
      };
      
      // Start the first attempt immediately
      attemptFill();
    } catch (error) {
      // console.log('Error in auto-fill function:', error);
    }
  }
  
  // Function to add smooth transitions and hide filters
  function addSmoothTransitions() {
    try {
      // Add CSS for smooth transitions
      const style = document.createElement('style');
      style.textContent = `
        .boost-pfs-no-results-enhancement {
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.4s ease-out, transform 0.4s ease-out;
        }
        .boost-pfs-no-results-enhancement.visible {
          opacity: 1;
          transform: translateY(0);
        }
        .boost-pfs-no-results-enhancement .categories-section {
          opacity: 0;
          transform: translateY(-20px);
          transition: opacity 0.5s ease-out 0.2s, transform 0.5s ease-out 0.2s;
        }
        .boost-pfs-no-results-enhancement.visible .categories-section {
          opacity: 1;
          transform: translateY(0);
        }
        
        /* Form loading animations */
        .hubspot-form-section {
          transition: height 0.5s ease-out, opacity 0.3s ease-out;
          overflow: hidden;
        }
        .hubspot-form-section.loading {
          height: 0 !important;
          opacity: 0;
        }
        .hubspot-form-section.loaded {
          opacity: 1;
        }
        
        /* Collection page element transitions */
        .collection__toolbar,
        .collection__products-count {
          transition: opacity 0.4s ease-out, height 0.4s ease-out, margin 0.4s ease-out, padding 0.4s ease-out;
          overflow: hidden;
        }
        .collection__toolbar.hiding,
        .collection__products-count.hiding {
          opacity: 0;
          height: 0 !important;
          margin: 0 !important;
          padding: 0 !important;
        }
        
        /* Filters section smooth transitions */
        .layout__section--secondary {
          transition: opacity 0.4s ease-out, transform 0.4s ease-out, width 0.4s ease-out, margin 0.4s ease-out, padding 0.4s ease-out;
          overflow: hidden;
        }
        .layout__section--secondary.hiding {
          opacity: 0;
          transform: translateX(-20px);
          width: 0 !important;
          margin: 0 !important;
          padding: 0 !important;
        }
        
        .collection__title {
          transition: text-align 0.4s ease-out;
        }
        .collection__title.centered {
          text-align: center !important;
        }
        
        /* Grid adjustments for 6/3 layout */
        .boost-pfs-no-results-enhancement .grid.grid-uniform {
          grid-template-columns: repeat(3, 1fr);
        }
        
        /* Mobile layout fix */
        @media (max-width: 768px) {
          .boost-pfs-no-results-enhancement {
            padding: 0 15px !important;
            margin-left: -15px;
            margin-right: -15px;
            box-sizing: border-box;
          }
          
          .boost-pfs-no-results-enhancement .hubspot-form-section {
            padding: 0 10px;
            margin-bottom: 30px;
          }
          
          .boost-pfs-no-results-enhancement .categories-section {
            padding: 0 10px;
            margin-bottom: 30px;
          }
          
          .boost-pfs-no-results-enhancement .grid.grid-uniform {
            grid-template-columns: 1fr !important;
            gap: 15px !important;
            max-width: 100% !important;
          }
          
          .boost-pfs-no-results-enhancement .grid__item {
            max-width: 100% !important;
            margin: 0 !important;
          }
          
          .boost-pfs-filter-message.result-empty-search {
            padding: 0 15px;
            box-sizing: border-box;
          }
          
          /* Ensure form elements are mobile friendly */
          #hubspot-form-container {
            max-width: 100%;
            overflow-x: hidden;
          }
          
          #hubspot-form-container form {
            max-width: 100% !important;
          }
          
          #hubspot-form-container .hs-form-field {
            margin-bottom: 15px !important;
          }
          
          #hubspot-form-container input,
          #hubspot-form-container textarea {
            max-width: 100% !important;
            box-sizing: border-box !important;
          }
        }
        /* HubSpot form responsive width styling */
        #hubspot-form-container {
          width: 90% !important;
          margin: 0 auto !important;
        }
        #hubspot-form-container .hs-form {
          width: 100% !important;
          min-width: 100% !important;
        }
        #hubspot-form-container .hs-form-field {
          margin-bottom: 1rem !important;
        }
        #hubspot-form-container .hs-input {
          width: 100% !important;
          min-width: 100% !important;
        }
        #hubspot-form-container .hs-fieldtype-textarea .hs-input {
          width: 100% !important;
          min-width: 100% !important;
          resize: vertical !important;
        }
        /* More aggressive HubSpot form styling */
        #hubspot-form-container * {
          box-sizing: border-box !important;
        }
        #hubspot-form-container iframe {
          width: 100% !important;
          min-width: 100% !important;
        }
        #hubspot-form-container .hbspt-form {
          width: 100% !important;
          min-width: 100% !important;
        }
        #hubspot-form-container .hs-form-iframe {
          width: 100% !important;
          min-width: 100% !important;
        }
        #hubspot-form-container .hs-form-target {
          width: 100% !important;
          min-width: 100% !important;
        }
        /* Prevent initial narrow width during loading */
        #hubspot-form-container:empty {
          min-height: 300px !important;
          width: 90% !important;
          margin: 0 auto !important;
          background: transparent !important;
        }
        /* Responsive breakpoints */
        @media (max-width: 768px) {
          #hubspot-form-container {
            max-width: 100% !important;
            padding: 0 10px !important;
          }
        }
        @media (max-width: 480px) {
          #hubspot-form-container {
            padding: 0 5px !important;
          }
        }
      `;
      document.head.appendChild(style);
      
      // Hide filters smoothly on no results
      const filtersSection = document.querySelector('.layout__section--secondary');
      if (filtersSection) {
        filtersSection.classList.add('hiding');
      }
      
      // Smoothly hide collection toolbar and products count
      const toolbar = document.querySelector('.collection__toolbar');
      const productsCount = document.querySelector('.collection__products-count');
      
      if (toolbar) {
        toolbar.classList.add('hiding');
      }
      
      if (productsCount) {
        productsCount.classList.add('hiding');
      }
      
      // Smoothly center the collection title
      const collectionTitle = document.querySelector('.collection__title');
      if (collectionTitle) {
        collectionTitle.classList.add('centered');
      }
      
    } catch (error) {
      // console.log('Error adding smooth transitions:', error);
    }
  }
  
  // Function to clean up enhancement content when there are results
  function cleanupEnhancementContent() {
    try {
      // Remove any existing enhancement content
      const existingEnhancement = document.querySelector('.boost-pfs-no-results-enhancement');
      if (existingEnhancement) {
        existingEnhancement.remove();
      }
      
    } catch (error) {
      // console.log('Error cleaning up enhancement content:', error);
    }
  }
  
  // Function to enhance no results content
  async function enhanceNoResultsContent(query) {
    try {
      // console.log('Enhancing no results content for query:', query);
      
      // Add smooth transitions first
      addSmoothTransitions();
      
      // Find the empty search message container
      const messageContainer = document.querySelector('.boost-pfs-filter-message.result-empty-search');
      if (!messageContainer) {
        // console.log('Could not find message container');
        return;
      }
      
      // Check if we already enhanced this container
      if (messageContainer.querySelector('.boost-pfs-no-results-enhancement')) {
        // console.log('Already enhanced this container');
        return;
      }
      
      // Create enhancement container
      const enhancementDiv = document.createElement('div');
      enhancementDiv.className = 'boost-pfs-no-results-enhancement';
      enhancementDiv.style.cssText = `
        margin-top: 30px;
        padding: 0 20px;
      `;
      
      // Add HubSpot form section (start with loading state)
      const formSection = document.createElement('div');
      formSection.className = 'hubspot-form-section loading';
      formSection.style.cssText = `
        margin-bottom: 40px;
        text-align: center;
      `;
      
      const formTitle = document.createElement('h3');
      formTitle.textContent = 'Need help finding what you\'re looking for?';
      formTitle.style.cssText = 'margin-bottom: 20px; color: #333;';
      
      const formDesc = document.createElement('p');
      formDesc.textContent = 'Our experts are here to help! Fill out the form below and we\'ll get back to you.';
      formDesc.style.cssText = 'margin-bottom: 20px; color: #666;';
      
      const formContainer = document.createElement('div');
      formContainer.id = 'hubspot-form-container';
      
      formSection.appendChild(formTitle);
      formSection.appendChild(formDesc);
      formSection.appendChild(formContainer);
      enhancementDiv.appendChild(formSection);
      
      // Add divider (will show after form loads)
      const divider = document.createElement('hr');
      divider.style.cssText = `
        border: none;
        border-top: 1px solid #e0e0e0;
        margin: 40px 0;
        opacity: 0;
        transition: opacity 0.3s ease-out;
      `;
      enhancementDiv.appendChild(divider);
      
      // Append enhancement to message container first
      messageContainer.appendChild(enhancementDiv);
      
      // Trigger initial animation
      setTimeout(() => {
        enhancementDiv.classList.add('visible');
      }, 100);
      
      // Function to add categories section
      async function addCategoriesSection() {
        // console.log('Adding categories section...');
        
        const matchingCollections = await findMatchingCollections(query);
        
        if (matchingCollections.length > 0) {
          // Show divider
          divider.style.opacity = '1';
          
          // Add categories section
          const categoriesSection = document.createElement('div');
          categoriesSection.className = 'categories-section';
          categoriesSection.style.cssText = `
            text-align: center;
            margin-bottom: 40px;
            opacity: 0;
            transform: translateY(-20px);
            transition: opacity 0.5s ease-out, transform 0.5s ease-out;
          `;
          
          const categoriesTitle = document.createElement('h3');
          categoriesTitle.textContent = 'Browse these related categories:';
          categoriesTitle.style.cssText = `
            margin-bottom: 30px;
            color: #333;
            font-size: 1.2em;
          `;
          categoriesSection.appendChild(categoriesTitle);
          
          // Create categories grid using existing product grid classes
          const categoriesGrid = document.createElement('div');
          categoriesGrid.className = 'grid grid-uniform';
          categoriesGrid.style.cssText = `
            display: grid;
            gap: 20px;
            margin: 0 auto;
            max-width: 1000px;
          `;
          
          matchingCollections.forEach(collection => {
            const categoryItem = document.createElement('div');
            categoryItem.className = 'grid__item';
            categoryItem.style.cssText = `
              background: #f8f8f8;
              border: 1px solid #e0e0e0;
              border-radius: 8px;
              padding: 20px;
              text-align: center;
              transition: transform 0.2s ease, box-shadow 0.2s ease;
            `;
            
            // Add hover effect
            categoryItem.addEventListener('mouseenter', () => {
              categoryItem.style.transform = 'translateY(-2px)';
              categoryItem.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
            });
            
            categoryItem.addEventListener('mouseleave', () => {
              categoryItem.style.transform = 'translateY(0)';
              categoryItem.style.boxShadow = 'none';
            });
            
            // Create title element with forced styles to ensure rendering
            const titleDiv = document.createElement('div');
            titleDiv.style.cssText = 'margin-bottom: 15px; min-height: 30px; display: flex; align-items: center; justify-content: center;';
            
            const titleLink = document.createElement('a');
            titleLink.href = collection.url;
            titleLink.style.cssText = 'text-decoration: none; color: #333 !important; display: block;';
            titleLink.textContent = collection.title;
            
            // Force text styles to ensure visibility
            titleLink.style.fontSize = '18px';
            titleLink.style.fontWeight = '600';
            titleLink.style.lineHeight = '1.2';
            titleLink.style.minHeight = '22px';
            
            titleDiv.appendChild(titleLink);
            categoryItem.appendChild(titleDiv);
            
            // Create button element
            const buttonLink = document.createElement('a');
            buttonLink.href = collection.url;
            buttonLink.textContent = 'Browse Category';
            buttonLink.style.cssText = `
              display: inline-block; 
              background: #333; 
              color: white; 
              padding: 10px 20px; 
              text-decoration: none; 
              border-radius: 4px; 
              font-weight: 500;
              transition: background-color 0.2s ease;
              font-size: 14px;
              line-height: 1.4;
            `;
            
            buttonLink.addEventListener('mouseenter', () => {
              buttonLink.style.backgroundColor = '#555';
            });
            
            buttonLink.addEventListener('mouseleave', () => {
              buttonLink.style.backgroundColor = '#333';
            });
            
            categoryItem.appendChild(buttonLink);
            categoriesGrid.appendChild(categoryItem);
          });
          
          categoriesSection.appendChild(categoriesGrid);
          enhancementDiv.appendChild(categoriesSection);
          
          // Animate categories in
          setTimeout(() => {
            categoriesSection.style.opacity = '1';
            categoriesSection.style.transform = 'translateY(0)';
          }, 300);
        }
      }
      
      // Function to handle form loading completion
      function onFormLoadComplete() {
        // console.log('Form loading complete, expanding form section...');
        
        // Get the actual height needed
        formSection.classList.remove('loading');
        formSection.style.height = 'auto';
        const autoHeight = formSection.offsetHeight;
        formSection.style.height = '0px';
        
        // Animate to full height
        setTimeout(() => {
          formSection.style.height = autoHeight + 'px';
          formSection.classList.add('loaded');
          
          // Remove height after transition completes
          setTimeout(() => {
            formSection.style.height = 'auto';
            
            // Add categories after form is fully loaded and expanded
            setTimeout(() => {
              addCategoriesSection();
            }, 800);
          }, 500);
        }, 50);
      }
      
      // Function to handle form loading failure
      function onFormLoadFailed() {
        // console.log('Form loading failed, showing categories without form...');
        
        // Hide form section completely
        formSection.style.display = 'none';
        
        // Add categories immediately
        addCategoriesSection();
      }
      
      // Function to create the HubSpot form with loading tracking
      function createHubSpotForm(searchQuery) {
        // console.log('Attempting to create HubSpot form with query:', searchQuery);
        
        const maxRetries = 10;
        const maxWaitTime = 15000; // 15 seconds max wait
        let retryCount = 0;
        let formLoadStartTime = Date.now();
        
        // Set a timeout to show categories even if form never loads
        const fallbackTimeout = setTimeout(() => {
          // console.log('Form loading timeout reached, showing categories...');
          onFormLoadFailed();
        }, maxWaitTime);
        
        const tryCreateForm = () => {
          retryCount++;
          // console.log(`Form creation attempt ${retryCount}/${maxRetries}`);
          
          try {
            if (window.hbspt && window.hbspt.forms && typeof window.hbspt.forms.create === 'function') {
              // console.log('HubSpot forms API available, creating form...');
              
              window.hbspt.forms.create({
                portalId: "23918152",
                formId: "7e3e28b7-d9dc-4128-9c19-b254d974131b",
                region: "na1",
                target: '#hubspot-form-container',
                onFormReady: function() {
                  // console.log('HubSpot form ready - applying width styling and completing load');
                  clearTimeout(fallbackTimeout);
                  
                  // Force width styling immediately when form is ready
                  const container = document.getElementById('hubspot-form-container');
                  if (container) {
                    const forms = container.querySelectorAll('form, .hs-form, iframe');
                    forms.forEach(form => {
                      form.style.width = '100%';
                      form.style.minWidth = '100%';
                      form.style.maxWidth = '100%';
                    });
                  }
                  
                  // Form loaded successfully
                  onFormLoadComplete();
                  
                  // Start auto-fill after form is ready
                  setTimeout(() => autoFillHubSpotForm(searchQuery), 3000);
                },
                onFormSubmit: function() {
                  // console.log('HubSpot form submitted successfully');
                }
              });
              
              // console.log('HubSpot form created successfully');
              return; // Success, exit the retry loop
            } else {
              // console.log('HubSpot forms API not available yet...');
              
              if (retryCount < maxRetries && (Date.now() - formLoadStartTime) < maxWaitTime) {
                // console.log(`Retrying in 1 second... (attempt ${retryCount + 1}/${maxRetries})`);
                setTimeout(tryCreateForm, 1000);
              } else {
                // console.error('Max retries reached or timeout - HubSpot form creation failed');
                clearTimeout(fallbackTimeout);
                onFormLoadFailed();
              }
            }
          } catch (error) {
            // console.error('Error creating HubSpot form:', error);
            
            if (retryCount < maxRetries && (Date.now() - formLoadStartTime) < maxWaitTime) {
              // console.log(`Retrying after error in 1 second... (attempt ${retryCount + 1}/${maxRetries})`);
              setTimeout(tryCreateForm, 1000);
            } else {
              // console.error('Max retries reached after errors - showing fallback');
              clearTimeout(fallbackTimeout);
              onFormLoadFailed();
            }
          }
        };
        
        // Start the form creation process
        tryCreateForm();
      }
      
      // Load HubSpot form script if not already loaded
      if (!window.hbspt) {
        // console.log('Loading HubSpot script...');
        const script = document.createElement('script');
        script.src = '//js.hsforms.net/forms/embed/v2.js';
        script.charset = 'utf-8';
        script.type = 'text/javascript';
        
        script.onload = () => {
          // console.log('HubSpot script loaded successfully');
          // Wait a bit for initialization then create form
          setTimeout(() => createHubSpotForm(query), 500);
        };
        
        script.onerror = () => {
          // console.error('Failed to load HubSpot script');
          onFormLoadFailed();
        };
        
        document.head.appendChild(script);
      } else {
        // console.log('HubSpot already loaded, creating form...');
        setTimeout(() => createHubSpotForm(query), 100);
      }
      
      // console.log('Successfully started no results content enhancement');
      
    } catch (error) {
      // console.error('Error enhancing no results content:', error);
    }
  }
  
  // Function to check for product results with multiple selectors and retry logic
  function checkForProductResults() {
    try {
      // Try multiple selectors for product items
      const selectors = [
        '.boost-pfs-filter-product-item',
        '.boost-pfs-filter-product-item-template',
        '.product-item',
        '.grid-product',
        '.product-card',
        '[data-product-item]'
      ];
      
      let productItems = [];
      let usedSelector = '';
      
      for (const selector of selectors) {
        const items = document.querySelectorAll(selector);
        if (items.length > 0) {
          productItems = items;
          usedSelector = selector;
          break;
        }
      }
      
      // console.log(`Found ${productItems.length} product items using selector: ${usedSelector}`);
      
      // Also check for "no results" message as a fallback
      const noResultsMessage = document.querySelector('.boost-pfs-filter-message.result-empty-search');
      const hasNoResultsMessage = noResultsMessage && noResultsMessage.offsetParent !== null;
      
      // console.log('Has no results message:', hasNoResultsMessage);
      
              if (productItems.length === 0 && hasNoResultsMessage) {
          const query = getSearchQuery();
          // console.log('No results confirmed for query:', query);
        
        if (query) {
          // Trigger GA4 event
          triggerGA4Event(query);
          
          // Enhance the no results content
          enhanceNoResultsContent(query);
        }
              } else if (productItems.length > 0) {
          // Clean up any existing enhancement content when there are results
          // console.log('Results found, cleaning up enhancement content...');
          cleanupEnhancementContent();
        } else {
          // If we can't determine either way, wait a bit more and retry
          // console.log('Unable to determine results state, will retry...');
          setTimeout(checkForProductResults, 1000);
        }
      
    } catch (error) {
      // console.error('Error checking for product results:', error);
    }
  }
  
  // Main function to handle Boost PFS rendered event
  function handleBoostRendered() {
    try {
      // console.log('Boost PFS rendered event fired, checking for results...');
      
      // Wait a bit for the DOM to settle, then check for results
      setTimeout(checkForProductResults, 500);
      
    } catch (error) {
      // console.error('Error in handleBoostRendered:', error);
    }
  }
  
  // Initialize the script
  function init() {
    try {
      // console.log('Boost Search No Results Enhancement script initialized');
      
      // Listen for Boost PFS rendered event
      document.addEventListener('boost-pfs:rendered', handleBoostRendered);
      
      // Also check on initial page load in case the event already fired
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
          setTimeout(handleBoostRendered, 500);
        });
      } else {
        setTimeout(handleBoostRendered, 500);
      }
    } catch (error) {
      // console.error('Error initializing script:', error);
    }
  }
  
  // Start the script
  init();
  
})();